import type React from "react";
import { useEffect, useId, useRef } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import type { emergencyMap } from "@duress/shared";
import { BRAND } from "@/lib/colors";
import { fmtInt } from "@/lib/format";
import { EmptyState } from "@/components/EmptyState";

type EmergencyMapPoint = emergencyMap.EmergencyMapPoint;

// Frontend-visible by necessity (the Maps JS API loads directly in the
// browser) — restricted by HTTP referrer in the Google Cloud Console, not by
// keeping it secret. See packages/frontend/.env.local (gitignored).
const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

interface GoogleMapsLibraries {
  core: google.maps.CoreLibrary;
  maps: google.maps.MapsLibrary;
  marker: google.maps.MarkerLibrary;
}

// A single loader/script-injection for the whole app, however many maps get
// mounted — the Maps JS API errors if you try to load its script twice.
let mapsLoaderPromise: Promise<GoogleMapsLibraries> | null = null;
function loadGoogleMaps(): Promise<GoogleMapsLibraries> {
  if (!mapsLoaderPromise) {
    const loader = new Loader({ apiKey: GOOGLE_MAPS_KEY ?? "", version: "weekly" });
    mapsLoaderPromise = Promise.all([
      loader.importLibrary("core"),
      loader.importLibrary("maps"),
      loader.importLibrary("marker"),
    ]).then(([core, maps, marker]) => ({ core, maps, marker }));
  }
  return mapsLoaderPromise;
}

// Every client's data is Australian (geocoding is restricted to
// countrycode=au), but the initial view also frames New Zealand for
// context — cap pan/zoom to that combined region with some sea margin so
// an accidental scroll-out can't wander off to a blank world view.
const AUSTRALIA_MAX_BOUNDS = { north: -6, south: -50, east: 190, west: 75 };

// The initial view always frames Australia + Tasmania + New Zealand,
// regardless of how tightly clustered a client's points are — fitting to
// points alone could zoom in past that if every point happened to be, say,
// in NSW. `minZoom` is locked to whatever zoom fitBounds() settles on for
// this view (see the 'idle' listener below), so users can zoom in for
// detail but can't scroll back out past this framing.
const AUSTRALIA_VIEW_BOUNDS = { north: -10, south: -47, east: 179, west: 112 };

// A standard dark theme in the Maps JS API's own styling schema (feature
// types / element types / stylers), matching the app's dark card palette
// rather than Google's default light road map.
const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#bdbdbd" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#181818" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#373737" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [{ color: "#4e4e4e" }],
  },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "transit", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#1b2733" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d5a73" }] },
];

function escapeHtml(raw: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return raw.replace(/[&<>"']/g, (c) => map[c] ?? c);
}

// `max-width` + `overflow-wrap` here (rather than relying on the
// InfoWindow's own `maxWidth` option alone) keeps a long address or a
// team name with no natural break point from overflowing into Google's
// default scrollable info-window box — the messy scrollbar seen in
// production before this was Google's own overflow handling kicking in.
function popupHtml(count: number, addressRaw: string, teamNames: string[]): string {
  return `<div style="font-family: Inter, system-ui, sans-serif; max-width: 240px; overflow-wrap: break-word;">
    <strong>${fmtInt(count)} events</strong><br/>
    <span style="opacity:0.8;">${escapeHtml(addressRaw)}</span>
    ${
      teamNames.length > 0
        ? `<br/><span style="opacity:0.6; font-size:12px;">${escapeHtml(teamNames.join(", "))}</span>`
        : ""
    }
  </div>`;
}

// A plain `google.maps.Marker` with a custom SVG icon (encoding the circle
// + count as an image), not the newer `AdvancedMarkerElement` — the
// advanced element only gets full custom-content support with a Map ID
// configured in the Cloud Console, which is a separate manual setup step
// beyond just the API key. This works with nothing but the key.
function markerIcon(count: number): google.maps.Icon {
  // Area-proportional-ish sizing so a store with far more events doesn't
  // just look "a bit bigger" than one with a couple — matches the previous
  // MapLibre marker's sizing exactly.
  const size = Math.max(20, Math.min(44, 16 + Math.sqrt(count) * 6));
  const radius = size / 2 - 1;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${String(size)}" height="${String(size)}">
    <circle cx="${String(size / 2)}" cy="${String(size / 2)}" r="${String(radius)}" fill="${BRAND.red}" stroke="white" stroke-width="2" />
    <text x="50%" y="52%" text-anchor="middle" dominant-baseline="central" fill="white" font-family="Inter, system-ui, sans-serif" font-size="11" font-weight="600">${String(count)}</text>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
}

/**
 * The emergency heat map itself — a real Google Maps JS API map with a pin
 * per point. Swapped in from MapLibre GL JS + MapTiler (see the plan doc)
 * once it became clear MapTiler's non-commercial-only free tier and its
 * $30/mo paid plan weren't worth carrying for a Duress-internal tool when
 * Google Maps Platform's own free tier has no such restriction.
 */
export function EmergencyMapChart({
  points,
  interactive = false,
}: {
  points: EmergencyMapPoint[];
  interactive?: boolean;
}): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!containerRef.current || points.length === 0 || !GOOGLE_MAPS_KEY) return;
    let cancelled = false;
    // See the Marker-deprecation justification comment further down.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const markers: google.maps.Marker[] = [];
    let activeInfoWindow: google.maps.InfoWindow | null = null;

    loadGoogleMaps()
      .then((libs) => {
        if (cancelled || !containerRef.current) return;

        const bounds = new libs.core.LatLngBounds(
          { lat: AUSTRALIA_VIEW_BOUNDS.south, lng: AUSTRALIA_VIEW_BOUNDS.west },
          { lat: AUSTRALIA_VIEW_BOUNDS.north, lng: AUSTRALIA_VIEW_BOUNDS.east },
        );
        for (const p of points) bounds.extend({ lat: p.lat, lng: p.lng });

        const map = new libs.maps.Map(containerRef.current, {
          // An explicit initial center/zoom is required here — leaving the
          // viewport to be established solely by the fitBounds() call below
          // leaves the map's projection permanently unresolved (getZoom()/
          // getCenter() stay undefined, 'idle' never fires, no tiles ever
          // load) even though markers still render fine since they're
          // placed independently of tiles. Verified directly against the
          // production Maps JS build.
          center: { lat: -25.27, lng: 133.775 },
          zoom: 4,
          styles: DARK_MAP_STYLE,
          restriction: { latLngBounds: AUSTRALIA_MAX_BOUNDS, strictBounds: false },
          // "cooperative" on the small preview card lets drag-to-pan and
          // ctrl+scroll-to-zoom work directly without hijacking a normal
          // page scroll over the card (which "greedy" does — a real issue
          // hit in production). The dialog is a focused overlay with
          // nothing to accidentally scroll past, so "greedy" there matches
          // its own "drag to pan, scroll... to zoom" description.
          gestureHandling: interactive ? "greedy" : "cooperative",
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        });
        map.fitBounds(bounds, 40);
        // Lock the zoomed-out limit to whatever fitBounds() settles on for
        // this view, once — users can zoom in for detail but can't scroll
        // back out past the Australia + NZ framing.
        libs.core.event.addListenerOnce(map, "idle", () => {
          const settledZoom = map.getZoom();
          if (settledZoom !== undefined) map.setOptions({ minZoom: settledZoom });
        });

        for (const point of points) {
          // `Marker` (not the newer `AdvancedMarkerElement`) deliberately —
          // advanced markers with custom content need a Map ID configured in
          // the Cloud Console, a separate setup step beyond the API key.
          // Marker itself isn't scheduled for removal (Google commits to 12
          // months' notice); revisit once a Map ID exists.
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          const marker = new libs.marker.Marker({
            position: { lat: point.lat, lng: point.lng },
            map,
            icon: markerIcon(point.count),
            title: `${fmtInt(point.count)} events at ${point.addressRaw}`,
          });
          const infoWindow = new libs.maps.InfoWindow({
            content: popupHtml(point.count, point.addressRaw, point.teamNames),
            maxWidth: 260,
          });
          // Two Google-internal quirks fixed up directly here — neither has
          // a public InfoWindow option, so this runs once Google has
          // rendered the popup's DOM for this open:
          // 1. `.gm-style-iw-d` hardcodes `overflow: scroll` (not `auto`)
          //    regardless of whether the content actually overflows — with
          //    our fixed max-width it always fits, so the scrollbar is
          //    pure visual noise.
          // 2. `.gm-style-iw-chr` (the row holding the close button) is a
          //    full 48x48px tap target by default, leaving a tall dead
          //    strip above short content like ours. Shrunk to a size that
          //    still comfortably fits the "×" while matching the content's
          //    own scale.
          infoWindow.addListener("domready", () => {
            const scrollable = document.querySelector(".gm-style-iw-d");
            if (scrollable instanceof HTMLElement) scrollable.style.overflow = "visible";
            const container = scrollable?.closest(".gm-style-iw-c");
            if (container instanceof HTMLElement) container.style.overflow = "visible";
            const closeButtonRow = container?.querySelector(".gm-style-iw-chr");
            if (closeButtonRow instanceof HTMLElement) {
              closeButtonRow.style.height = "28px";
              closeButtonRow.style.minHeight = "28px";
              const closeButton = closeButtonRow.querySelector("button");
              if (closeButton instanceof HTMLElement) {
                closeButton.style.width = "28px";
                closeButton.style.height = "28px";
                // The "×" icon inside is a fixed 24px + 12px margin (sized
                // for the button's original 48px box: 24 + 12 + 12 = 48) —
                // shrinking only the button leaves the icon overflowing
                // past its new edges. Scale it down to match (16 + 6 + 6 =
                // 28), verified live to sit centred with no clipping.
                const icon = closeButton.firstElementChild;
                if (icon instanceof HTMLElement) {
                  icon.style.width = "16px";
                  icon.style.height = "16px";
                  icon.style.margin = "6px";
                }
              }
            }
          });
          marker.addListener("click", () => {
            activeInfoWindow?.close();
            infoWindow.open({ map, anchor: marker });
            activeInfoWindow = infoWindow;
          });
          markers.push(marker);
        }
      })
      .catch(() => {
        // Loader failure (bad/missing key, network) — the map area is left
        // blank rather than throwing; the empty-state check below already
        // covers the "no key at all" case, and a transient load failure
        // here is rare enough not to warrant its own UI state.
      });

    return () => {
      cancelled = true;
      markers.forEach((m) => {
        m.setMap(null);
      });
    };
  }, [points, interactive]);

  if (points.length === 0) {
    return (
      <EmptyState
        title="No locations to show"
        body="No emergency events in this window resolved to a mappable address."
      />
    );
  }

  if (!GOOGLE_MAPS_KEY) {
    return (
      <EmptyState
        title="Map unavailable"
        body="Missing VITE_GOOGLE_MAPS_API_KEY — add it to packages/frontend/.env.local."
      />
    );
  }

  const summary = `Map showing ${fmtInt(points.length)} location${points.length === 1 ? "" : "s"} with emergency activity across ${fmtInt(points.reduce((sum, p) => sum + p.count, 0))} total events.`;

  return (
    <div className="relative">
      <h3 id={titleId} className="sr-only">
        Emergency location map
      </h3>
      <p className="sr-only">{summary}</p>
      <div
        ref={containerRef}
        role="img"
        aria-labelledby={titleId}
        style={{ height: interactive ? 560 : 500, borderRadius: 8, overflow: "hidden" }}
      />
    </div>
  );
}
